import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Operadoras from "@/pages/Operadoras";
import DIDs from "@/pages/DIDs";
import Ramais from "@/pages/Ramais";
import Grupos from "@/pages/Grupos";
import Filas from "@/pages/Filas";
import IVRs from "@/pages/IVRs";
import Rotas from "@/pages/Rotas";
import Anuncios from "@/pages/Anuncios";
import ACL from "@/pages/ACL";
import CDR from "@/pages/CDR";
import LiveChannels from "@/pages/LiveChannels";
import AntiFraude from "@/pages/AntiFraude";
import FreeSwitch from "@/pages/FreeSwitch";
import Health from "@/pages/Health";
import Backups from "@/pages/Backups";
import Audit from "@/pages/Audit";
import Users from "@/pages/Users";
import CiscoProvisioning from "@/pages/CiscoProvisioning";
import MusicOnHold from "@/pages/MusicOnHold";
import CallCenterAgents from "@/pages/CallCenterAgents";
import AgentConsole from "@/pages/AgentConsole";
import TimeConditions from "@/pages/TimeConditions";
import Tenants from "@/pages/Tenants";
import Certificates from "@/pages/Certificates";
import YealinkProvisioning from "@/pages/YealinkProvisioning";
import Registrations from "@/pages/Registrations";
import Recordings from "@/pages/Recordings";
import CallCenterReports from "@/pages/CallCenterReports";
import Workforce from "@/pages/Workforce";
import SupervisorDashboard from "@/pages/SupervisorDashboard";
import FeatureCodes from "@/pages/FeatureCodes";
import TeamsIntegration from "@/pages/TeamsIntegration";
import AgentPortal from "@/pages/AgentPortal";
import EnterpriseReports from "@/pages/EnterpriseReports";
import CallCenterWallboard from "@/pages/CallCenterWallboard";
import CallPermissions from "@/pages/CallPermissions";
import OutboundCallPins from "@/pages/OutboundCallPins";
import Voicemail from "@/pages/Voicemail";
import GeoIP from "@/pages/GeoIP";
import SIPProtection from "@/pages/SIPProtection";
import PauseReasons from "@/pages/PauseReasons";
import PickupGroups from "@/pages/PickupGroups";
import PublicExtensionMonitor from "@/pages/PublicExtensionMonitor";
import Licenses from "@/pages/Licenses";
import SystemDateTime from "@/pages/SystemDateTime";
import ExtensionMaintenance from "@/pages/ExtensionMaintenance";
import AudioGenerator from "@/pages/AudioGenerator";
import SetupWizard from "@/pages/SetupWizard";
import CallTest from "@/pages/CallTest";
import OperationalQuality from "@/pages/OperationalQuality";
import ConfigurationDiagnostics from "@/pages/ConfigurationDiagnostics";

function Protected() {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-xs font-mono text-[color:var(--text-muted)]"
        data-testid="app-loading">
        Verificando sessão…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "operator") return <Navigate to="/agente/painel" replace />;
  if (user.role === "supervisor") {
    const allowed = location.pathname === "/call-center/supervisao"
      || location.pathname === "/painel-tv"
      || location.pathname.startsWith("/call-center/relatorios");
    if (!allowed) return <Navigate to="/call-center/supervisao" replace />;
  }
  return <Layout />;
}

function LoginRoute() {
  const { user } = useAuth();
  const location = useLocation();
  if (user) {
    if (user.role === "operator") return <Navigate to="/agente/painel" replace />;
    if (location.pathname === "/master" && ["manager", "supervisor"].includes(user.role)) return <Navigate to="/call-center/supervisao" replace />;
    return <Navigate to="/" replace />;
  }
  return <Login />;
}

function AgentProtected() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-xs font-mono">Verificando sessao...</div>;
  if (!user) return <Navigate to="/agente" replace />;
  if (user.role !== "operator") return <Navigate to="/" replace />;
  return <AgentPortal />;
}

function WallboardProtected() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-[#111418] text-white flex items-center justify-center">Carregando painel...</div>;
  if (!user) return <Navigate to="/master" replace />;
  if (user.role === "operator") return <Navigate to="/agente/painel" replace />;
  return <CallCenterWallboard />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/master" element={<LoginRoute />} />
            <Route path="/agente" element={<LoginRoute />} />
            <Route path="/agente/painel" element={<AgentProtected />} />
            <Route path="/:tenantSlug/agentes" element={<LoginRoute />} />
            <Route path="/:tenantSlug/console-agente" element={<AgentProtected />} />
            <Route path="/painel-tv" element={<WallboardProtected />} />
            <Route path="/monitoramento/:tenantSlug" element={<PublicExtensionMonitor />} />
            <Route path="/" element={<Protected />}>
              <Route index element={<Dashboard />} />
              <Route path="assistente-configuracao" element={<SetupWizard />} />
              <Route path="teste-chamada" element={<CallTest />} />
              <Route path="operadoras" element={<Operadoras />} />
              <Route path="dids" element={<DIDs />} />
              <Route path="ramais" element={<Ramais />} />
              <Route path="grupos" element={<Grupos />} />
              <Route path="filas" element={<Filas />} />
              <Route path="ivrs" element={<IVRs />} />
              <Route path="rotas" element={<Rotas />} />
              <Route path="anuncios" element={<Anuncios />} />
              <Route path="acl" element={<ACL />} />
              <Route path="cdr" element={<CDR />} />
              <Route path="live" element={<LiveChannels />} />
              <Route path="antifraud" element={<AntiFraude />} />
              <Route path="freeswitch" element={<FreeSwitch />} />
              <Route path="health" element={<Health />} />
              <Route path="backups" element={<Backups />} />
              <Route path="audit" element={<Audit />} />
              <Route path="usuarios" element={<Users />} />
              <Route path="provisionamento-cisco" element={<CiscoProvisioning />} />
              <Route path="musica-espera" element={<MusicOnHold />} />
              <Route path="callcenter-agentes" element={<CallCenterAgents />} />
              <Route path="console-agente" element={<AgentConsole />} />
              <Route path="condicoes-tempo" element={<TimeConditions />} />
              <Route path="clientes" element={<Tenants />} />
              <Route path="certificados" element={<Certificates />} />
              <Route path="clientes/cadastro" element={<Tenants />} />
              <Route path="clientes/licencas" element={<Licenses />} />
              <Route path="call-center/filas" element={<Filas />} />
              <Route path="call-center/agentes" element={<CallCenterAgents />} />
              <Route path="call-center/opcoes-pausa" element={<PauseReasons />} />
              <Route path="call-center/mensagens-atendimento" element={<Anuncios />} />
              <Route path="call-center/mensagens-espera" element={<MusicOnHold />} />
              <Route path="call-center/relatorios" element={<Navigate to="/call-center/relatorios/visao-geral" replace />} />
              <Route path="call-center/relatorios/visao-geral" element={<CallCenterReports section="overview" />} />
              <Route path="call-center/relatorios/filas" element={<CallCenterReports section="queues" />} />
              <Route path="call-center/relatorios/agentes" element={<CallCenterReports section="agents" />} />
              <Route path="call-center/relatorios/chamadas" element={<CallCenterReports section="calls" />} />
              <Route path="call-center/relatorios/recebidas" element={<CallCenterReports section="received" />} />
              <Route path="call-center/relatorios/realizadas" element={<CallCenterReports section="placed" />} />
              <Route path="call-center/relatorios/perdidas" element={<CallCenterReports section="missed" />} />
              <Route path="call-center/relatorios/abandonadas" element={<CallCenterReports section="abandoned" />} />
              <Route path="call-center/relatorios/ura" element={<CallCenterReports section="ivr" />} />
              <Route path="call-center/relatorios/gravacoes" element={<CallCenterReports section="recordings" />} />
              <Route path="call-center/dimensionamento" element={<Workforce />} />
              <Route path="call-center/supervisao" element={<SupervisorDashboard />} />
              <Route path="ramais/cadastro" element={<Ramais />} />
              <Route path="ramais/permissoes" element={<CallPermissions />} />
              <Route path="ramais/pins-ligacao" element={<OutboundCallPins />} />
              <Route path="ramais/monitorar" element={<Registrations />} />
              <Route path="ramais/manutencao" element={<ExtensionMaintenance />} />
              <Route path="ramais/correio-voz" element={<Voicemail />} />
              <Route path="ramais/grupos" element={<Grupos />} />
              <Route path="ramais/grupos-captura" element={<PickupGroups />} />
              <Route path="troncos/cadastro" element={<Operadoras />} />
              <Route path="troncos/dids" element={<DIDs />} />
              <Route path="troncos/rotas-entrada" element={<DIDs />} />
              <Route path="troncos/condicoes-tempo" element={<TimeConditions />} />
              <Route path="troncos/ura" element={<IVRs />} />
              <Route path="relatorios/chamadas-ativas" element={<LiveChannels />} />
              <Route path="relatorios/ligacoes" element={<CDR />} />
              <Route path="relatorios/telefonia" element={<EnterpriseReports mode="telephony" />} />
              <Route path="relatorios/dashboard" element={<EnterpriseReports mode="dashboard" />} />
              <Route path="relatorios/chamadas" element={<EnterpriseReports mode="calls" />} />
              <Route path="relatorios/agentes" element={<EnterpriseReports mode="agents" />} />
              <Route path="relatorios/filas" element={<EnterpriseReports mode="queues" />} />
              <Route path="relatorios/sla" element={<EnterpriseReports mode="sla" />} />
              <Route path="relatorios/campanhas" element={<EnterpriseReports mode="campaigns" />} />
              <Route path="relatorios/clientes" element={<EnterpriseReports mode="clients" />} />
              <Route path="relatorios/gravacoes" element={<EnterpriseReports mode="recordings" />} />
              <Route path="relatorios/qualidade" element={<EnterpriseReports mode="quality" />} />
              <Route path="relatorios/qualidade-operacional" element={<OperationalQuality />} />
              <Route path="relatorios/tempo-real" element={<EnterpriseReports mode="realtime" />} />
              <Route path="relatorios/auditoria" element={<EnterpriseReports mode="audit" />} />
              <Route path="relatorios/personalizados" element={<EnterpriseReports mode="templates" />} />
              <Route path="relatorios/bi" element={<EnterpriseReports mode="bi" />} />
              <Route path="relatorios/multiempresa" element={<EnterpriseReports mode="companies" />} />
              <Route path="lcr/rotas-saida" element={<Rotas />} />
              <Route path="lcr/regras-discagem" element={<Rotas />} />
              <Route path="lcr/permissoes" element={<CallPermissions />} />
              <Route path="lcr/condicoes-tempo" element={<TimeConditions />} />
              <Route path="sistema/saude" element={<Health />} />
              <Route path="sistema/diagnostico" element={<ConfigurationDiagnostics />} />
              <Route path="sistema/musica-espera" element={<MusicOnHold />} />
              <Route path="sistema/anuncios" element={<Anuncios />} />
              <Route path="sistema/gerador-audios" element={<AudioGenerator />} />
              <Route path="sistema/freeswitch" element={<FreeSwitch />} />
              <Route path="sistema/backups" element={<Backups />} />
              <Route path="administrativo/antifraude" element={<AntiFraude />} />
              <Route path="administrativo/certificados" element={<Certificates />} />
              <Route path="administrativo/microsoft-teams" element={<TeamsIntegration />} />
              <Route path="provisionamento/cisco" element={<CiscoProvisioning />} />
              <Route path="provisionamento/yealink" element={<YealinkProvisioning />} />
              <Route path="auditoria" element={<Audit />} />
              <Route path="registros" element={<Registrations />} />
              <Route path="sistema/registros" element={<Registrations />} />
              <Route path="sistema/monitoramento-registros" element={<Registrations />} />
              <Route path="sistema/gravacoes" element={<Recordings />} />
              <Route path="sistema/codigos-facilidade" element={<FeatureCodes />} />
              <Route path="sistema/data-hora" element={<SystemDateTime />} />
              <Route path="sistema/geoip" element={<GeoIP />} />
              <Route path="sistema/protecao-sip" element={<SIPProtection />} />
            </Route>
          
            <Route path="/:tenantSlug" element={<LoginRoute />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
